/**
 * @file
 * Inspiration Configurator behavior.
 *
 * Handles:
 *  - WAI-ARIA Tabs keyboard navigation (arrow keys, roving tabindex)
 *  - Radio item selection within each tab panel
 *  - Composited preview: shows/hides pre-rendered layerImage markup per item
 *    within each overlay layer — no src/alt swapping needed
 *  - Screen-reader live-region announcements on preview change
 *
 * Image strategy:
 *  All images are Drupal-rendered markup (slots / |raw strings). Each overlay
 *  layer contains one .inspiration-configurator__layer-source[data-item-id] div
 *  per item, with the rendered image inside. On selection, JS shows the matching
 *  source div and hides all others within that layer.
 */

((Drupal, once) => {
  'use strict';

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Update the visible overlay layer for a given tab by showing the source div
   * that matches the selected item and hiding all others.
   *
   * Each tab owns one overlay layer. Updating one tab does not affect any
   * other tab — all overlays remain composited simultaneously on top of the
   * base image.
   */
  const updateLayer = (configurator: HTMLElement, tabId: string, itemId: string): void => {
    const layer = configurator.querySelector<HTMLElement>(
      `.inspiration-configurator__layer--overlay[data-layer-tab-id="${tabId}"]`
    );
    if (!layer) {
      return;
    }

    layer.querySelectorAll<HTMLElement>('.inspiration-configurator__layer-source').forEach((sourceDiv) => {
      sourceDiv.hidden = sourceDiv.dataset.itemId !== itemId;
    });
  };

  /**
   * Announce a preview update to screen readers via the polite live region.
   */
  const announcePreviewUpdate = (configurator: HTMLElement): void => {
    const status = configurator.querySelector<HTMLElement>('.inspiration-configurator__sr-status');
    if (!status) {
      return;
    }
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = Drupal.t('Preview updated.');
    });
  };

  // ---------------------------------------------------------------------------
  // Tab management
  // ---------------------------------------------------------------------------

  /**
   * Activate a tab: update ARIA states, roving tabindex, show/hide panels.
   */
  const activateTab = (
    configurator: HTMLElement,
    targetBtn: HTMLButtonElement,
    allTabButtons: HTMLButtonElement[]
  ): void => {
    const tabId = targetBtn.dataset.tabId;
    if (!tabId) {
      return;
    }

    allTabButtons.forEach((btn) => {
      const isTarget = btn.dataset.tabId === tabId;
      btn.setAttribute('aria-selected', String(isTarget));
      btn.setAttribute('tabindex', isTarget ? '0' : '-1');
      btn.classList.toggle('inspiration-configurator__tab--active', isTarget);
    });

    configurator.querySelectorAll<HTMLElement>('.inspiration-configurator__panel').forEach((panel) => {
      panel.hidden = panel.dataset.panelTabId !== tabId;
    });
  };

  /**
   * Handle keyboard navigation within the tablist (WAI-ARIA Tabs pattern).
   */
  const handleTabKeydown = (event: KeyboardEvent, configurator: HTMLElement, tabs: HTMLButtonElement[]): void => {
    const current = event.target as HTMLButtonElement;
    const currentIndex = tabs.indexOf(current);
    if (currentIndex === -1) {
      return;
    }

    let newIndex: number | null = null;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    if (newIndex !== null && tabs[newIndex]) {
      tabs[newIndex].focus();
      activateTab(configurator, tabs[newIndex], tabs);
    }
  };

  // ---------------------------------------------------------------------------
  // Item (radio) management
  // ---------------------------------------------------------------------------

  /**
   * Select an item: sync --selected modifier and refresh the preview overlay.
   */
  const selectItem = (configurator: HTMLElement, radio: HTMLInputElement): void => {
    const tabId = radio.dataset.tabId ?? '';
    const itemId = radio.dataset.itemId ?? '';

    if (!tabId || !itemId) {
      return;
    }

    // Sync --selected modifier on all item labels within this panel
    const panel = configurator.querySelector<HTMLElement>(
      `.inspiration-configurator__panel[data-panel-tab-id="${tabId}"]`
    );
    if (panel) {
      panel.querySelectorAll<HTMLElement>('.inspiration-configurator__item').forEach((label) => {
        const input = label.querySelector<HTMLInputElement>('.inspiration-configurator__item-radio');
        label.classList.toggle('inspiration-configurator__item--selected', input?.value === itemId);
      });
    }

    // Update the overlay layer from the hidden source pool
    updateLayer(configurator, tabId, itemId);
    announcePreviewUpdate(configurator);
  };

  // ---------------------------------------------------------------------------
  // Drupal behavior
  // ---------------------------------------------------------------------------

  /**
   * Initialises all inspiration-configurator components on the page.
   *
   * @description
   * Seeds each overlay layer from the default-checked radio (first item per tab),
   * then attaches tab keyboard navigation and radio change handling.
   * Uses once() for AJAX compatibility.
   */
  Drupal.behaviors.inspirationConfigurator = {
    attach(context: Document | HTMLElement): void {
      const configurators = once('inspiration-configurator', '.inspiration-configurator', context);

      configurators.forEach((element) => {
        const configurator = element as HTMLElement;

        // ── Tab list ────────────────────────────────────────────────────────
        const tabList = configurator.querySelector<HTMLElement>('[data-configurator-tablist]');
        if (!tabList) {
          return;
        }

        const tabButtons = Array.from(tabList.querySelectorAll<HTMLButtonElement>('[role="tab"]'));

        if (tabButtons.length === 0) {
          return;
        }

        tabButtons.forEach((btn) => {
          btn.addEventListener('click', () => {
            activateTab(configurator, btn, tabButtons);
            // Move focus into the panel so the next Tab key navigates content
            const panelId = btn.getAttribute('aria-controls');
            if (panelId) {
              configurator.querySelector<HTMLElement>(`#${panelId}`)?.focus();
            }
          });
        });

        tabList.addEventListener('keydown', (event) => {
          handleTabKeydown(event as KeyboardEvent, configurator, tabButtons);
        });

        // ── Radio items ─────────────────────────────────────────────────────
        const radios = Array.from(
          configurator.querySelectorAll<HTMLInputElement>('.inspiration-configurator__item-radio')
        );

        if (radios.length === 0) {
          return;
        }

        radios.forEach((radio) => {
          radio.addEventListener('change', () => {
            if (radio.checked) {
              selectItem(configurator, radio);
            }
          });
        });

        // ── Seed overlay layers from the default-checked radios ─────────────
        // The first radio per group is checked in HTML, so this runs once on
        // init to populate the visible layer <img> elements from the source pool.
        radios
          .filter((r) => r.checked)
          .forEach((r) => {
            updateLayer(configurator, r.dataset.tabId ?? '', r.dataset.itemId ?? '');
          });
      });
    },
  };
})(Drupal, once);
